import DialogSelectPrompt from "@/components/prompt/dialog-select-prompt";
import { addPromptToHistory } from "@/components/prompt/prompts-popover";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToastError } from "@/components/ui/use-toast";
import { useChatActionEmitter } from "@/hooks/useChatActions";
import { usePinnedPrompts } from "@/hooks/usePromptsPinned";
import { useTranslation } from "@/hooks/useTranslation";
import {
  buildPromptSnapshot,
  getPromptContent,
  getPromptId,
  getPromptStyle,
  getPromptTitle,
  getSupportedLang,
} from "@/lib/prompt-utils";
import { cn } from "@/lib/utils";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PromptDTO } from "@shaarp/common";
import { MAX_PINNED_PROMPTS } from "@/enums/pinned-prompts";
import { ChevronRightIcon, PencilIcon, Plus, XIcon, ZapIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { createPortal } from "react-dom";

type SortableQuickActionProps = {
  prompt: PromptDTO;
  lang: "fr" | "en";
  isPending: boolean;
  onPromptClick: (prompt: PromptDTO) => void;
  onUnpin: (prompt: PromptDTO) => void;
};

function SortableQuickAction({
  prompt,
  lang,
  isPending,
  onPromptClick,
  onUnpin,
}: SortableQuickActionProps) {
  const promptId = getPromptId(prompt);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: promptId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const { Icon, colors } = getPromptStyle(prompt);
  const title = getPromptTitle(prompt, lang);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn("group/item relative", isDragging && "z-50 opacity-0")}
    >
      <button
        className={cn(
          "flex flex-row gap-4 items-center rounded-xl border border-border bg-background px-5 py-5 text-left text-sm font-medium transition-colors w-full touch-none",
          isPending
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-muted cursor-pointer",
        )}
        onClick={() => onPromptClick(prompt)}
        disabled={isPending}
        {...attributes}
        {...listeners}
      >
        <div
          className={cn(
            "shrink-0 rounded-full flex items-center justify-center w-9 h-9",
            colors.iconBg,
          )}
        >
          <Icon className={cn("w-5 h-5", colors.icon)} />
        </div>
        <span className="flex-1 line-clamp-2">{title}</span>
        <ChevronRightIcon className="w-4 h-4 shrink-0 text-muted-foreground" />
      </button>
      {/* Hover zone in the top-right corner */}
      <div className="absolute -top-1.5 -right-1.5 z-10">
        <button
          type="button"
          className="group/unpin p-0.5 rounded-full opacity-0 cursor-pointer transition-all duration-150 bg-muted text-muted-foreground hover:bg-red-500 hover:text-white group-hover/item:opacity-100"
          onClick={(e) => {
            e.stopPropagation();
            onUnpin(prompt);
          }}
          aria-label="Unpin prompt"
        >
          <XIcon className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function DragOverlayCard({
  prompt,
  lang,
}: {
  prompt: PromptDTO;
  lang: "fr" | "en";
}) {
  const { Icon, colors } = getPromptStyle(prompt);
  const title = getPromptTitle(prompt, lang);

  return (
    <div
      className={cn(
        "flex flex-row gap-4 items-center rounded-xl border border-border bg-background px-5 py-5 text-left text-sm font-medium w-full",
        "scale-[1.02] rotate-[1deg] animate-in fade-in duration-200",
        "shadow-xl ring-2 ring-primary/20",
      )}
    >
      <div
        className={cn(
          "shrink-0 rounded-full flex items-center justify-center w-9 h-9",
          colors.iconBg,
        )}
      >
        <Icon className={cn("w-5 h-5", colors.icon)} />
      </div>
      <span className="flex-1 line-clamp-2">{title}</span>
      <ChevronRightIcon className="w-4 h-4 shrink-0 text-muted-foreground" />
    </div>
  );
}

export default function CardQuickActions() {
  const { sendChatMessage, isPending } = useChatActionEmitter();
  const { t, i18n } = useTranslation();
  const lang = getSupportedLang(i18n.language);
  const toastError = useToastError();
  const { pinnedPrompts, togglePinned, reorderPinned, isLoading } =
    usePinnedPrompts({
      context: "company",
      fetchCustomPrompts: true,
      listenForChanges: true,
    });

  const handleTogglePinned = useCallback(
    (prompt: PromptDTO) => {
      togglePinned(prompt).catch(() => toastError());
    },
    [togglePinned, toastError],
  );

  const [showSelectDialog, setShowSelectDialog] = useState(false);
  const [activePrompt, setActivePrompt] = useState<PromptDTO | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const prompt = pinnedPrompts.find(
        (p) => getPromptId(p) === event.active.id,
      );
      setActivePrompt(prompt ?? null);
    },
    [pinnedPrompts],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActivePrompt(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = pinnedPrompts.findIndex(
        (p) => getPromptId(p) === active.id,
      );
      const newIndex = pinnedPrompts.findIndex(
        (p) => getPromptId(p) === over.id,
      );

      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(pinnedPrompts, oldIndex, newIndex);
      reorderPinned(reordered).catch(() => toastError());
    },
    [pinnedPrompts, reorderPinned, toastError],
  );

  const canAddMore = pinnedPrompts.length < MAX_PINNED_PROMPTS.company;

  const handlePinnedPromptClick = useCallback(
    (prompt: PromptDTO) => {
      if (isPending) return;
      const content = getPromptContent(prompt, lang);
      addPromptToHistory(prompt, undefined, lang);
      sendChatMessage(content, buildPromptSnapshot(prompt, "", lang));
    },
    [isPending, lang, sendChatMessage],
  );

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-lg">
            <div className="flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-muted">
              <ZapIcon className="w-5 h-5" />
            </div>
            {t("company.insights.quickActions")}
            <div className="flex items-center gap-2 ml-auto">
              {isLoading ? (
                <Skeleton className="h-5 w-10 rounded-full" />
              ) : (
                <span className="text-xs font-medium text-muted-foreground bg-muted rounded-full px-2 py-0.5">
                  {pinnedPrompts.length}/{MAX_PINNED_PROMPTS.company}
                </span>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground"
                    onClick={() => setShowSelectDialog(true)}
                  >
                    <PencilIcon className="w-3.5 h-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("company.insights.editTactics")}
                </TooltipContent>
              </Tooltip>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="grid grid-cols-1 gap-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-[68px] rounded-xl" />
              ))}
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={pinnedPrompts.map((p) => getPromptId(p))}
                strategy={verticalListSortingStrategy}
              >
                <div className="grid grid-cols-1 gap-3">
                  {pinnedPrompts.map((prompt) => (
                    <SortableQuickAction
                      key={getPromptId(prompt)}
                      prompt={prompt}
                      lang={lang}
                      isPending={isPending}
                      onPromptClick={handlePinnedPromptClick}
                      onUnpin={handleTogglePinned}
                    />
                  ))}
                  {canAddMore && (
                    <button
                      type="button"
                      className={cn(
                        "flex flex-row gap-4 items-center rounded-xl border-2 border-dashed border-muted-foreground/30 bg-transparent px-5 py-5 text-left text-sm font-medium transition-colors",
                        isPending
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:border-primary/50 hover:bg-muted/30 cursor-pointer",
                      )}
                      onClick={() => setShowSelectDialog(true)}
                      disabled={isPending}
                    >
                      <Plus className="w-5 h-5 shrink-0 text-muted-foreground" />
                      <span className="flex-1 text-muted-foreground">
                        {t("prompt.pinned.empty.label")}
                      </span>
                    </button>
                  )}
                </div>
              </SortableContext>
              {createPortal(
                <DragOverlay
                  dropAnimation={{
                    duration: 200,
                    easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
                  }}
                >
                  {activePrompt ? (
                    <DragOverlayCard prompt={activePrompt} lang={lang} />
                  ) : null}
                </DragOverlay>,
                document.body,
              )}
            </DndContext>
          )}
        </CardContent>
      </Card>

      {showSelectDialog && (
        <DialogSelectPrompt
          context="company"
          onSelect={(prompt: PromptDTO) => {
            handleTogglePinned(prompt);
            setShowSelectDialog(false);
          }}
          onClose={() => setShowSelectDialog(false)}
        />
      )}
    </>
  );
}
